import * as R from 'ramda';
// These functions need to be updated in monjin/data repo post successful integration.

import { ContentRange } from './model';

const ACCEPT = 'Accept';
const CONTENT_TYPE = 'Content-Type';
const APP_JSON = 'application/json';

const defaultHeaders = new Headers({
    [CONTENT_TYPE]: APP_JSON
});

export type MethodType = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface ValidationError {

    code: 422;
    message: string;
    errors: Array<{
        field: any;
        fieldName: string;
        message: string;
    }>;
}

interface ResponseStandard<T = object> {
    code: number;
    headers: Headers;
    body: T;
}

interface Response204 {
    code: 204;
    headers: Headers;
}


interface Response206 extends ResponseStandard {
    code: 206;
    range: ContentRange;
}

interface Response401 {
    code: 401;
}

interface Response422 {
    code: 422;
    headers: Headers;
    body: ValidationError;
}

interface ResponseFailed {
    code: 'failed';
}

export interface ResponseSuccPatterns {
    // Default success handler
    2000: (x: any) => void;

    200: (x: ResponseStandard) => void;
    201: (x: ResponseStandard) => void;
    204: (x: Response204) => void;
    206: (x: Response206) => void;
}

export interface ResponseErrorPatterns {
    // Default client error handler
    4000: (x: any) => void;

    // Default server error handler
    5000: (x: any) => void;

    403: (x: ResponseStandard) => void;
    404: (x: ResponseStandard) => void;
    409: (x: ResponseStandard) => void;
    410: (x: ResponseStandard) => void;
    416: (x: ResponseStandard) => void;
    422: (x: Response422) => void;

    // When fetch itself has failed
    networkError: (reason: string) => void;

    // Global error handler
    anyError: (x: any) => void;
}


export function makeRequest(method: MethodType, url: string, body: any, headers = defaultHeaders) {
    return new Request(url, {
        method, headers,
        body: JSON.stringify(body),
        credentials: 'same-origin'
    });
}

/**
 * Creates a json GET request.
 * WARNING: Mutates the header if you pass
 */
export function makeGet(url: string, headers = new Headers()) {
    headers.append(ACCEPT, APP_JSON);
    return makeRequest('GET', url, undefined, headers);
}

/**
 * Returns URLSearchParams for given object
 * @param queryObject key-value pair of query string params
 */
export function makeSearchParams(queryObject: object) {
    const param = new URLSearchParams();

    Object.keys(queryObject)
        .filter((key) => queryObject[key])
        .map((key) => param.set(key, queryObject[key]));

    return param;
}

/**
 * Make ContentRange into <unit>=<range-start>-<range-end>
 */
export function makeRangeHeader(top: number, skip: number, unit: string = 'items') {
    return unit + '=' + (skip + 1) + '-' + (skip + top);
}

export function fetchBody<T>(request: Request) {
    return fetchData<T>(request)
        .then((x) => (x as any).body);
}

const isOkStatus = (code: number) => code >= 200 && code < 300;

export function matchError(projection: Partial<ResponseErrorPatterns>) {

    return function _matchError(err: any) {

        const code = err.code;

        if (code >= 400 && code < 500) {
            // Handle client side errors
            if (projection[code]) {
                return projection[code](err);
            } else if (projection[4000]) {
                return projection[4000](err);
            } else if (projection.anyError) {
                return projection.anyError(err);
            } else {
                // What to do when no match
                throw err;
            }
        } else if (code >= 500 && code < 600) {
            // Handle server side errors
            if (projection[code]) {
                return projection[code](err);
            } else if (projection[5000]) {
                return projection[5000](err);
            } else if (projection.anyError) {
                return projection.anyError(err);
            } else {
                // What to do when no match
                throw err;
            }
        } else if (code === 'failed') {
            if (projection.networkError) {
                return projection.networkError('Network error');
            } else if (projection.anyError) {
                return projection.anyError(err);
            }
        }

        // I am not sure if this is an API error
        throw err;
    };
}

export function fetchData<T = any>(request: Request) {

    return fetch(request)
        .then((x) => handleResponse<T>(x))
        .catch((err) => {

            // If known error then throw as it is
            if (err instanceof RangeError || err.code) {
                throw err;
            }

            // Genuine fetch API failure
            return Promise.reject({ code: 'failed' } as ResponseFailed);
        });
}

function handleResponse<T>(response: Response): Promise<Response204 | Response206 | ResponseStandard<T>> {
    const code = response.status;

    // 204 response has no body.
    if (code === 204) {
        return Promise.resolve({ code: 204, headers: response.headers } as Response204);
    }

    // 206 is yet to be handled
    if (code === 206) {
        makeStandardResponse<T>(response)
            .then(parseContentRangeResponse)
            .then((range) => ({
                code: 206,
                headers: response.headers,
                body: response.body,
                range
            }) as Response206);
    }

    // Attempt to parse standard JSON or text response for body
    if (isOkStatus(code)) {
        return makeStandardResponse<T>(response);
    }

    // For 401, we don't care about body.
    if (code === 401) {
        const event = new CustomEvent('monjin__token-expired');
        window.dispatchEvent(event);

        return Promise.reject({ code: 401 } as Response401);
    }

    // Attempt to parse standard JSON or text response for body
    // Also, reject code
    if (code >= 400 && code < 500) {
        return makeStandardResponse(response)
            .then((x) => Promise.reject(x));
    }

    // Some unknown stuff
    return response.text()
        .then((body: any) =>
            Promise.reject({ code, body, headers: response.headers } as ResponseStandard<T>));

}

function makeStandardResponse<T>(response: Response): Promise<ResponseStandard<T>> {
    return extractBody(response)
        .then((body) => ({ code: response.status, body, headers: response.headers }));
}

function extractBody(response: Response) {
    return isJSON(response)
        ? response.json()
        : response.text();
}

function isJSON(response: Response): boolean {
    const type = (response.headers.get('content-type') || '').toLowerCase();

    return type.includes('application/json');
}

// Matches typical content range header
// <unit> <range-start>-<range-end>/<size>
// items 10-20/100
const rangeHeaderRegEx = /(\S+)\s(\d+)-(\d+)\/(\d+)$/;

export function formatRangeHeader(range: string): ContentRange | null {

    // TODO: Temporary
    const groups = rangeHeaderRegEx.exec('items ' + range);

    return !groups ? null : {
        first: Number(groups[2]),
        last: Number(groups[3]),
        total: Number(groups[4]),
        unit: groups[1]
    };
}

export function parseContentRangeResponse<T>(x: ResponseStandard<T>) {

    const range = x.headers.get('Content-Range');

    if (range) {
        const parsed = formatRangeHeader(range);

        if (parsed) {
            return parsed;
        } else {
            throw new RangeError('Invalid Content-Range header');
        }
    } else {
        throw new RangeError('No Content-Range header');
    }
}

export const checkArray = R.ifElse(
    R.isNil,
    R.always([]),
    (x) => x
);
